import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavigationMenu } from 'radix-ui'
import clsx from 'clsx'
import Image from 'next/image'
import NextLink from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '../utils/cn'
import { type Icon, type MenuItem } from './typings'
import { isStaticImageData, getSubmenuIcon, isSamePath } from './utils'

export const Submenu = ({
  menuItem,
  onSubMenuItemClick,
}: {
  menuItem: MenuItem
  onSubMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
}) => {
  const path = usePathname()

  if (!menuItem.Children) {
    return null
  }

  const numRows = Math.ceil(Object.keys(menuItem.Children).length / 2)

  return (
    <div className='text-text-800 font-manrope text-body-md flex flex-col gap-4 p-3'>
      <ul
        className={cn(
          'grid grid-flow-col grid-cols-2 items-center gap-x-4 gap-y-2',
          // CRITICAL: DO NOT DELETE: Required for dynamic grid-rows-${numRows} compilation
          // values: grid-rows-2 grid-rows-3 grid-rows-4 grid-rows-5
          `grid-rows-${numRows}`,
        )}
      >
        {Object.entries(menuItem.Children).map(([_, value], i) => (
          <LinkMenuItem
            icon={getSubmenuIcon(value.icon)}
            key={i}
            onSubMenuItemClick={onSubMenuItemClick}
            path={value.path}
            subTitle={value.subTitle}
            title={value.title}
          />
        ))}
      </ul>
      {menuItem.More ? (
        <NavigationMenu.Link asChild>
          <NextLink
            className={cn(
              'text-body-md text-primary-700 flex w-fit items-center gap-2 p-3',
              'active:text-primary-800 hover:underline',
              { 'font-bold': isSamePath(path, menuItem.More.path) },
            )}
            href={menuItem.More.path}
            onClick={() => {
              onSubMenuItemClick?.({
                clickedElement: menuItem.More?.title ?? '',
                clickUrl: menuItem.More?.path ?? '/',
              })
            }}
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            prefetch={false}
            rel='noopener'
            target='_blank'
          >
            {menuItem.More.title}
            <FontAwesomeIcon icon={faArrowRight} />
          </NextLink>
        </NavigationMenu.Link>
      ) : undefined}
    </div>
  )
}

const LinkMenuItem = ({
  active = false,
  icon,
  path,
  onSubMenuItemClick,
  subTitle,
  title,
}: {
  active?: boolean
  icon: Icon
  path: string
  onSubMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
  subTitle?: string
  title: string
}) => {
  return (
    <NavigationMenu.Item>
      <NavigationMenu.Link
        asChild
        className={cn(
          'group flex items-center gap-2 rounded-lg p-3',
          'hover:bg-surface-tealgrey-50 focus:bg-surface-tealgrey-50',
          'active:bg-surface-tealgrey-200',
        )}
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <NextLink
          href={path}
          onClick={() => {
            onSubMenuItemClick?.({
              clickedElement: title,
              clickUrl: path,
            })
          }}
          prefetch={false}
          rel='noopener'
          target='_blank'
        >
          <MenuItemIcon icon={icon} />
          <div>
            <div
              className={cn({
                'text-primary-700 font-bold': active,
              })}
            >
              {title}
            </div>
            {subTitle ? (
              <span
                className={clsx('text-body-sm', {
                  'text-primary-700': active,
                  'text-text-400': !active,
                })}
              >
                {subTitle}
              </span>
            ) : null}
          </div>
        </NextLink>
      </NavigationMenu.Link>
    </NavigationMenu.Item>
  )
}

const MenuItemIcon = ({ icon }: { icon: Icon }) =>
  typeof icon === 'string' || isStaticImageData(icon) ? (
    <div className='h-10 w-10 shrink-0 overflow-hidden rounded-md'>
      <Image
        alt=''
        aria-hidden
        className='h-10 object-cover'
        height={40}
        src={isStaticImageData(icon) ? icon.src : icon}
        width={40}
      />
    </div>
  ) : (
    <div className='bg-primary-700 flex h-10 w-10 shrink-0 items-center justify-center rounded'>
      <FontAwesomeIcon
        className='text-surface-primary'
        icon={icon}
        size='lg'
        width={40}
      />
    </div>
  )
